import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectDetailsPageSubprojectsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { LoadingState } from 'modules/project_management/components/LoadingState'

import SubprojectsDataCollection from 'modules/project_management/components/tables/SubprojectsDataCollection'
import { SubprojectsSidepanelTabs } from 'modules/project_management/components/tables/SubprojectsDataCollection/SubprojectsSidepanel'

import { ISubprojectsPanelData } from './types'

const SubprojectsPage = () => {
  const { id } = useParams()
  const projectId = id ?? ''
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(ProjectManagementProjectDetailsPageSubprojectsPageDocument, {
    variables: { companyId: company.id, projectId },
  })

  const project = data?.projectManagement.project

  const hasSubprojectsEntityFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_projects_subprojects_entity'
    )?.enabled ?? false

  const budgetStrategy = project?.budgetStrategy || null

  const [showSubprojectsPanel, setShowSubprojectsPanel] = useState<boolean>(false)
  const [subprojectPanelData, setSubprojectPanelData] = useState<ISubprojectsPanelData>({
    inputedMinutes: 0,
    subprojectId: '0',
    laborCostCents: null,
    subprojectName: '',
  })

  useEffect(() => {
    track(trackingEvents.projectManagement.subprojects.goToProjectDetailsSubproject)
  }, [])

  const canSeeCosts = project?._policies?.seeCost.can ?? false
  const canSeeLaborCosts = project?._policies?.seeLaborCost.can ?? false

  const projectCurrency = project?.legalEntity.currency || 'EUR'

  const canManageProjects = data?.projectManagement._policies.projectManage.can ?? false
  const canManageSubprojects = project?._policies?.assignEditor.can ?? false
  const isOwnerOrAdmin = project?._policies?.assignOwner.can ?? false
  const hasTimeTrackingSection =
    data?.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can ?? false

  const openSidePanel = (sidePanelData: ISubprojectsPanelData) => {
    setShowSubprojectsPanel(true)
    setSubprojectPanelData(sidePanelData)

    track(trackingEvents.projectManagement.subprojects.openSubprojectDetails, {
      project_id: projectId,
      subproject_id: sidePanelData.subprojectId,
    })
  }

  if (loading || project == null) {
    return <LoadingState />
  }

  return (
    <Box gap="s24">
      <SubprojectsDataCollection
        projectId={projectId}
        canExportData={canManageProjects && hasTimeTrackingSection}
        canManageSubprojects={canManageSubprojects}
        canSeeCosts={canSeeCosts}
        hasSubprojectsEntityFeature={hasSubprojectsEntityFeature}
        projectCurrency={projectCurrency}
        onClick={openSidePanel}
        budgetStrategyDelegation={budgetStrategy?.delegated ?? false}
        budgetStrategyBudgetType={budgetStrategy?.budgetType ?? null}
        billableProject={project?.isBillable ?? false}
      />

      <SubprojectsSidepanelTabs
        subprojectId={subprojectPanelData.subprojectId}
        subprojectData={subprojectPanelData}
        onClose={() => setShowSubprojectsPanel(false)}
        showSubprojectsSidepanelOne={showSubprojectsPanel}
        projectId={projectId}
        projectCurrency={projectCurrency}
        canSeeLaborCosts={canSeeLaborCosts}
        subprojectEntityFeatureEnabled={hasSubprojectsEntityFeature}
        companyId={company.id}
        includeCost={canSeeCosts}
        projectDelegated={budgetStrategy?.delegated ?? false}
        isOwnerOrAdmin={isOwnerOrAdmin}
      />
    </Box>
  )
}

export default SubprojectsPage
